import React from 'react';
import * as styles from './TextField.css.ts';

/**
 * TextFieldコンポーネントのプロパティ
 */
export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** 入力欄の上に表示するラベル（オプション） */
  label?: string;
  /** エラーメッセージ。指定するとエラー状態とメッセージを表示 */
  error?: string;
  /** エラーがない場合に入力欄の下に表示するヘルプテキスト */
  helperText?: string;
  /** trueの場合、コンテナの幅いっぱいに表示 */
  fullWidth?: boolean;
  /** 入力欄のバリアント。デフォルトは'outlined' */
  variant?: 'outlined' | 'filled' | 'standard';
}

/**
 * ラベル、エラー、ヘルプテキストをサポートするテキスト入力フィールドコンポーネント
 *
 * @example
 * ```tsx
 * <TextField
 *   label="Username"
 *   placeholder="Enter username"
 *   value={username}
 *   onChange={(e) => setUsername(e.target.value)}
 * />
 *
 * <TextField
 *   label="Email"
 *   type="email"
 *   error={emailError}
 *   helperText="We'll never share your email"
 * />
 * ```
 */
export const TextField: React.FC<TextFieldProps> = ({
  label,
  error,
  helperText,
  fullWidth = false,
  variant = 'outlined',
  className = '',
  id,
  ...rest
}) => {
  const inputId = id || `text-field-${Math.random().toString(36).substr(2, 9)}`;

  const containerClass = `${styles.container} ${fullWidth ? styles.fullWidth : ''} ${className}`.trim();

  const inputClass = styles.input({
    variant,
    error: error ? true : undefined,
  });

  return (
    <div
      className={containerClass}
    >
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={inputClass}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
        {...rest}
      />
      {error && (
        <span id={`${inputId}-error`} className={styles.errorText}>
          {error}
        </span>
      )}
      {!error && helperText && (
        <span id={`${inputId}-helper`} className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
};